/**
 * Leave API Service
 */

import { apiClient } from '../client';
import type {
  ApiResponse,
  PaginatedData,
  LeaveInitData,
  LeaveBalance,
  LeaveType,
  LeaveApplication,
  LeaveApplicationDetail,
  LeaveApplyRequest,
  CalculateHoursRequest,
  CalculateHoursResult,
  Substitute,
  AllocationRecord,
} from '../../types';

const API_PREFIX = '/api/saiens/leave';

/**
 * Get leave init data (balance + types + time options)
 */
export async function getLeaveInit(): Promise<ApiResponse<LeaveInitData>> {
  const response = await apiClient.get<ApiResponse<LeaveInitData>>(
    `${API_PREFIX}/init`
  );
  return response.data;
}

/**
 * Get leave balance
 */
export async function getLeaveBalance(): Promise<ApiResponse<LeaveBalance[]>> {
  const response = await apiClient.get<ApiResponse<LeaveBalance[]>>(
    `${API_PREFIX}/balance`
  );
  return response.data;
}

/**
 * Get leave types
 */
export async function getLeaveTypes(): Promise<ApiResponse<LeaveType[]>> {
  const response = await apiClient.get<ApiResponse<LeaveType[]>>(
    `${API_PREFIX}/types`
  );
  return response.data;
}

/**
 * Get time options
 */
export async function getTimeOptions(): Promise<
  ApiResponse<{ hour_options: { id: number; value: number; name: string }[]; minute_options: { id: number; value: number; name: string }[] }>
> {
  const response = await apiClient.get<
    ApiResponse<{ hour_options: { id: number; value: number; name: string }[]; minute_options: { id: number; value: number; name: string }[] }>
  >(`${API_PREFIX}/time-options`);
  return response.data;
}

/**
 * Get substitutes
 */
export async function getSubstitutes(): Promise<ApiResponse<Substitute[]>> {
  const response = await apiClient.get<ApiResponse<Substitute[]>>(
    `${API_PREFIX}/substitutes`
  );
  return response.data;
}

/**
 * Calculate leave hours
 */
export async function calculateHours(
  data: CalculateHoursRequest
): Promise<ApiResponse<CalculateHoursResult>> {
  const response = await apiClient.post<ApiResponse<CalculateHoursResult>>(
    `${API_PREFIX}/calculate-hours`,
    data
  );
  return response.data;
}

interface LeaveListParams {
  offset?: number;
  limit?: number;
  state?: string;
}

/**
 * Get leave applications list
 */
export async function getLeaveApplications(
  params: LeaveListParams = {}
): Promise<ApiResponse<PaginatedData<LeaveApplication>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<LeaveApplication>>>(
    `${API_PREFIX}/applications`,
    { params }
  );
  return response.data;
}

/**
 * Get leave application detail
 */
export async function getLeaveApplication(
  id: number
): Promise<ApiResponse<LeaveApplicationDetail>> {
  const response = await apiClient.get<ApiResponse<LeaveApplicationDetail>>(
    `${API_PREFIX}/applications/${id}`
  );
  return response.data;
}

/**
 * Create leave application (draft)
 */
export async function createLeaveApplication(
  data: LeaveApplyRequest
): Promise<ApiResponse<LeaveApplicationDetail>> {
  const response = await apiClient.post<ApiResponse<LeaveApplicationDetail>>(
    `${API_PREFIX}/applications`,
    data
  );
  return response.data;
}

/**
 * Update leave application
 */
export async function updateLeaveApplication(
  id: number,
  data: Partial<LeaveApplyRequest>
): Promise<ApiResponse<LeaveApplicationDetail>> {
  const response = await apiClient.put<ApiResponse<LeaveApplicationDetail>>(
    `${API_PREFIX}/applications/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete leave application (draft only)
 */
export async function deleteLeaveApplication(
  id: number
): Promise<ApiResponse<void>> {
  const response = await apiClient.delete<ApiResponse<void>>(
    `${API_PREFIX}/applications/${id}`
  );
  return response.data;
}

/**
 * Submit leave application (draft → f_approve)
 */
export async function submitLeaveApplication(
  id: number
): Promise<ApiResponse<LeaveApplicationDetail>> {
  const response = await apiClient.post<ApiResponse<LeaveApplicationDetail>>(
    `${API_PREFIX}/applications/${id}/submit`
  );
  return response.data;
}

/**
 * Cancel leave application (f_approve → draft)
 */
export async function cancelLeaveApplication(
  id: number
): Promise<ApiResponse<LeaveApplicationDetail>> {
  const response = await apiClient.post<ApiResponse<LeaveApplicationDetail>>(
    `${API_PREFIX}/applications/${id}/cancel`
  );
  return response.data;
}

/**
 * Get all allocations (including expired)
 */
export async function getAllocations(): Promise<ApiResponse<AllocationRecord[]>> {
  const response = await apiClient.get<ApiResponse<AllocationRecord[]>>(
    `${API_PREFIX}/allocations`
  );
  return response.data;
}
